import { useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import { AppEvents } from "services/events";
import { KycStatus } from "types/BETypes";
import { showErrorModal } from "helpers";
import {
  isKycAccepted as getIsKycAccepted,
  isKycPending as getIsKycPending,
  isKycRejected as getIsKycRejected,
} from "helpers/bonus/kyc";
import { getErrorMessageFromResponse } from "helpers/shared/errors";
import useAuth from "hooks/useAuth";
import {
  EKycResponseType,
  IKYCProfileFormRef,
  KYCProfileFormType,
} from "components/CompanyBankAccountComponents/KYCVerificationWidget";
import {
  ECompanyCardResponseType,
  IPaidCardShippingDetailsFormRef,
  PaidCardShippingDetailsFormType,
} from "components/CompanyBankAccountComponents/PaidCardShippingDetailsForm";

import { EButtonsFlex, EModalTypes } from "uikit/Modal";

import {
  KycVerifyReqDto,
  mutationBankCardsControllerIssueBankCard,
  mutationBankCardsControllerKycVerify,
} from "utils/swagger_react_query";

import { COMPANY_CARD_CREATION_STEPS } from "./constants";
import {
  getCombinedUserDataWithFailedKycData,
  getFailedKycUserDataFromLocalStorage,
  getKycResponseType,
  profileFormValuesToKycRequestPayload,
  removeFailedKycUserDataFromLocalStorage,
  setFailedKycUserDataToLocalStorage,
  shippingDetailsFormValuesToRequestPayload,
} from "./helpers";
import { IUseCompanyBankAccountCreationModal } from "./types";

export const useCompanyBankAccountCreationModal = ({
  isOpen,
  setIsOpen,
  onFinished,
  onClose,
  refetchPageContentCallback,
}: IUseCompanyBankAccountCreationModal) => {
  const translationPrefix = `components.company_bank_account_components`;

  const { getCurrentUser } = useAuth();
  const [isLoading, setLoading] = useState<boolean>(true);
  const userDetailsFromStore = useAppSelector(userMetadataSelector);
  const currentUser = getCombinedUserDataWithFailedKycData(
    userDetailsFromStore,
    getFailedKycUserDataFromLocalStorage(),
  );

  const isKycAccepted = getIsKycAccepted(currentUser);
  const isKycPending = getIsKycPending(currentUser);
  const isKycRejected = getIsKycRejected(currentUser);

  const initialStep = isKycAccepted
    ? COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS
    : COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION;

  const [currentStep, setCurrentStep] = useState<COMPANY_CARD_CREATION_STEPS>(initialStep);

  const profileFormRef = useRef<IKYCProfileFormRef>(null);
  const [isKycVerifying, setIsKycVerifying] = useState<boolean>(false);
  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] = useState<boolean>(false);
  const [kycVerificationSvoc, setKycVerificationSvoc] = useState<boolean>(false);
  const [kycResponseType, setKycResponseType] = useState<EKycResponseType | undefined>(
    isKycRejected ? EKycResponseType.ERROR : undefined,
  );

  const shippingDetailsFormRef = useRef<IPaidCardShippingDetailsFormRef>(null);
  const [isShippingDetailsSubmitting, setIsShippingDetailsSubmitting] = useState<boolean>(false);
  const [shippingDetailsSvoc, setShippingDetailsSvoc] = useState<boolean>(false);
  const [isCardholderAgreementAccepted, setIsCardholderAgreementAccepted] =
    useState<boolean>(false);
  const [companyCardResponseType, setCompanyCardResponseType] = useState<
    ECompanyCardResponseType.SUCCESS | undefined
  >();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [showPromoSection, setShowPromoSection] = useState<boolean>(!currentUser?.kycStatus);

  const isPrimaryBtnDisabled: Record<COMPANY_CARD_CREATION_STEPS, boolean> = {
    [COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION]:
      !currentUser?.kycStatus || !kycResponseType ? !isTermsAndConditionsAccepted : false,
    [COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS]: !companyCardResponseType
      ? !isCardholderAgreementAccepted
      : false,
  };

  useEffect(() => {
    if (isOpen) {
      initFetch();

      return () => removeFailedKycUserDataFromLocalStorage();
    }
  }, [isOpen]);

  //Common
  const initFetch = async () => {
    try {
      setLoading(true);
      await getCurrentUser();
    } catch (error) {
      handleCloseModal(true, true);
      showErrorModal(error);
    } finally {
      setLoading(false);
    }
  };

  const showBackClickWarningModal = () => {
    const _prefix = `${translationPrefix}.shared.close_form_confirmation`;
    const message =
      currentStep === COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION ? "kyc" : "shipping";

    AppEvents.emit("SetGlobalModal", {
      className: "pay-distribution-redirection-warning-modal",
      isOpen: true,
      type: EModalTypes.WARNING,
      title: <Trans i18nKey={`${_prefix}.title`} />,
      message: <Trans i18nKey={`${_prefix}.${message}`} />,
      mainButton: {
        text: <Trans i18nKey={`${_prefix}.continue_btn`} />,
        onClick: () => {
          setIsOpen(true);
        },
        autoWidth: true,
      },
      secondaryButton: {
        text: <Trans i18nKey={`common.modal.cancel`} />,
        onClick: () => {
          handleCloseModal(true, true);
        },
        autoWidth: true,
      },
      buttonsFlex: EButtonsFlex.ROW_REVERSE,
    });
  };

  const handleBackBtnClick = () => {
    if (currentStep === COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION) {
      if (profileFormRef?.current?.dirty) {
        handleCloseModal?.(false);
        showBackClickWarningModal();
      } else {
        handleCloseModal?.();
      }
    }

    if (currentStep === COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS) {
      if (shippingDetailsFormRef?.current?.dirty) {
        handleCloseModal?.(false);
        showBackClickWarningModal();
      } else {
        handleCloseModal?.();
      }
    }
  };

  const handleCloseModal = (clearData: boolean = true, shouldRefetch?: boolean) => {
    if (clearData) {
      setIsTermsAndConditionsAccepted(false);
      setIsCardholderAgreementAccepted(false);
      setCurrentStep(initialStep);
    }
    setErrorMessage(undefined);
    removeFailedKycUserDataFromLocalStorage();
    setIsOpen(false);
    if (!showPromoSection && shouldRefetch) {
      refetchPageContentCallback?.();
    }
    onClose?.();
  };

  const handlePrimaryBtnClick = () => {
    if (currentStep === COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION) {
      if (isKycRejected) {
        setKycResponseType(undefined);
      } else if (isKycPending) {
        handleCloseModal(true, true);
      } else if (isKycAccepted) {
        setCurrentStep(COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS);
      }
    }

    if (
      currentStep === COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS &&
      companyCardResponseType === ECompanyCardResponseType.SUCCESS
    ) {
      handleCloseModal?.(true, true);
      onFinished?.();
    }
  };

  const handleShowPromoSection = () => {
    setShowPromoSection((prev) => !prev);
  };

  //KYC STEP
  const handleProfileFormSubmit = async (values: KYCProfileFormType) => {
    //Submit KYC verification
    try {
      setIsKycVerifying(true);

      const payload = profileFormValuesToKycRequestPayload(values);
      const failedUserData: KycVerifyReqDto | undefined = getFailedKycUserDataFromLocalStorage();

      if (isKycRejected && failedUserData) {
        const _kycResponseStatus = getKycResponseType(
          currentUser?.kycStatus as KycStatus,
          payload,
          failedUserData,
        );
        if (_kycResponseStatus === EKycResponseType.INCORRECT_INFO) {
          setKycResponseType(_kycResponseStatus);
          return;
        }
      }

      const response = await mutationBankCardsControllerKycVerify()(payload);
      setKycResponseType(EKycResponseType.VERIFICATION_IS_TAKING_TOO_LONG);

      const _kycResponseStatus = getKycResponseType(response?.kycStatus as KycStatus);

      //Result handling
      if (_kycResponseStatus === EKycResponseType.SUCCESS) {
        setKycResponseType(EKycResponseType.SUCCESS);
        removeFailedKycUserDataFromLocalStorage();
      } else if (_kycResponseStatus === EKycResponseType.ERROR) {
        setKycResponseType(_kycResponseStatus);
        setFailedKycUserDataToLocalStorage(payload);
      } else if (_kycResponseStatus === EKycResponseType.VERIFICATION_IS_TAKING_TOO_LONG) {
        setKycResponseType(EKycResponseType.VERIFICATION_IS_TAKING_TOO_LONG);
      }
    } catch (error) {
      setErrorMessage(getErrorMessageFromResponse(error));
    } finally {
      await initFetch();
      setIsKycVerifying(false);
      setIsTermsAndConditionsAccepted(false);
    }
  };

  const handleChangeTermsAndConditions = () => {
    setIsTermsAndConditionsAccepted((prev) => !prev);
  };

  const handleChangeCardholderAgreement = () => {
    setIsCardholderAgreementAccepted((prev) => !prev);
  };

  // SHIPPING DETAILS STEP
  const handleSubmitShippingDetails = async (values: PaidCardShippingDetailsFormType) => {
    try {
      setIsShippingDetailsSubmitting(true);
      const payload = shippingDetailsFormValuesToRequestPayload(values);
      await mutationBankCardsControllerIssueBankCard()(payload);

      //Result handling
      setCompanyCardResponseType(ECompanyCardResponseType.SUCCESS);
    } catch (error) {
      setErrorMessage(getErrorMessageFromResponse(error));
    } finally {
      setIsShippingDetailsSubmitting(false);
      setIsCardholderAgreementAccepted(false);
    }
  };

  const formSubmitter = () => {
    setErrorMessage(undefined);
    //Trigger form validation for KYC verification and shipping details
    if (currentStep === COMPANY_CARD_CREATION_STEPS.KYC_VERIFICATION) {
      setKycVerificationSvoc(true);
      profileFormRef?.current?.onSubmit();
    }
    if (currentStep === COMPANY_CARD_CREATION_STEPS.SHIPPING_DETAILS) {
      setShippingDetailsSvoc(true);
      shippingDetailsFormRef?.current?.onSubmit();
    }
  };

  return {
    metadata: {
      isLoading,
      currentUser,
      step: {
        currentStep,
        setCurrentStep,
      },
      isPrimaryBtnDisabled,
      kyc: {
        isKycAccepted,
        isKycPending,
        isKycRejected,
      },
    },
    pageData: {
      verificationStep: {
        isTermsAndConditionsAccepted,
        handleChangeTermsAndConditions,
        handleProfileFormSubmit,
        ref: profileFormRef,
        isKycVerifying,
        svoc: kycVerificationSvoc,
        kycResponseType,
      },
      shippingDetails: {
        isCardholderAgreementAccepted,
        handleChangeCardholderAgreement,
        handleSubmitShippingDetails,
        ref: shippingDetailsFormRef,
        isShippingDetailsSubmitting,
        svoc: shippingDetailsSvoc,
        companyCardResponseType,
      },
      errorMessage,
      promoSection: {
        showPromoSection,
      },
    },
    actions: {
      formSubmitter,
      handleBackBtnClick,
      handleShowPromoSection,
      handlePrimaryBtnClick,
    },
  };
};
